import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Link2 } from "lucide-react";
import { api, mediaUrl, parseLinks } from "../lib/api";
import { LanguageProvider, useLang, setSiteOverrides, pick } from "../lib/i18n";
import { LogoMark } from "../components/Logo";
import Seo from "../components/Seo";
import CaptionedImage from "../components/CaptionedImage";
import Navbar from "../components/site/Navbar";
import Footer from "../components/site/Footer";

function BlogDetailInner() {
  const { t, lang } = useLang();
  const { id } = useParams();
  const [post, setPost] = useState(null);
  const [settings, setSettings] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    api
      .get("/site")
      .then((r) => {
        setSiteOverrides(r.data.content || {});
        const found = (r.data.posts || []).find((p) => p.id === id);
        if (found) {
          setPost(found);
          setSettings(r.data.settings || {});
        } else {
          setNotFound(true);
        }
      })
      .catch(() => setNotFound(true));
  }, [id]);

  if (notFound) {
    return (
      <div data-testid="blog-detail-notfound" className="min-h-screen bg-[#FBF9F5] flex flex-col items-center justify-center gap-4">
        <p className="text-sm font-sans text-[#6E675F]">Story not found.</p>
        <Link to="/" className="text-[#9A7B66] font-sans font-semibold text-sm underline underline-offset-4">
          {t("vd.back")}
        </Link>
      </div>
    );
  }

  if (!post) {
    return (
      <div data-testid="blog-detail-loader" className="min-h-screen flex items-center justify-center bg-[#FBF9F5]">
        <LogoMark className="w-12 h-12 animate-pulse" color="#9A7B66" />
      </div>
    );
  }

  const title = pick(post, "title", lang);
  const backlinks = parseLinks(post.backlinks);

  return (
    <div data-testid="blog-detail-page" className="bg-[#FBF9F5] min-h-screen flex flex-col">
      <Seo
        title={`${title} | Nusadira Lembongan Villa Journal`}
        description={pick(post, "excerpt", lang)}
        image={mediaUrl(post.image_url)}
        imageAlt={post.image_alt || title}
      />
      <Navbar
        bookingUrl={settings?.booking_engine_url}
        logoUrl={settings?.logo_url}
        logoLightUrl={settings?.logo_light_url}
        navMenu={settings?.nav_menu}
        hiddenSections={String(settings?.hidden_sections || "").split(",").map((s) => s.trim()).filter(Boolean)}
      />

      <main className="flex-1">
        <section className="bg-[#1E1A17] pt-[120px] pb-12 px-5 sm:px-8">
          <div className="max-w-3xl mx-auto">
            <Link
              data-testid="blog-detail-back"
              to="/"
              className="inline-flex items-center gap-2 text-[11px] font-sans font-semibold uppercase tracking-[0.18em] text-[#C9A66B] hover:text-white transition-colors"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              {t("blog.back")}
            </Link>
            <div className="mt-8 flex items-center gap-3 text-[11px] uppercase tracking-[0.18em] font-sans font-semibold">
              <span className="text-[#C9A66B]">{pick(post, "category", lang)}</span>
              <span className="w-1 h-1 rounded-full bg-[#C9A66B]" />
              <span className="text-[#F4EFEA]/60">{post.read_time}</span>
            </div>
            <h1 data-testid="blog-detail-title" className="mt-4 font-serif font-normal tracking-tight text-[#F4EFEA] leading-tight text-3xl sm:text-4xl lg:text-5xl">
              {title}
            </h1>
          </div>
        </section>

        <motion.article
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="max-w-3xl mx-auto px-5 sm:px-8 py-12 sm:py-16"
        >
          {post.image_url && (
            <div className="rounded-md overflow-hidden h-72 sm:h-96 mb-10">
              <CaptionedImage
                testId="blog-detail-image"
                src={mediaUrl(post.image_url)}
                alt={post.image_alt || title}
                title={post.image_title || title}
                description={post.image_description || pick(post, "excerpt", lang)}
                caption={post.image_caption || ""}
                focalPoint={post.image_focal_point || "50% 0%"}
                className="w-full h-full"
                imageClassName="w-full h-full object-cover"
              />
            </div>
          )}
          <div className="space-y-5">
            {String(pick(post, "content", lang) || pick(post, "excerpt", lang) || "")
              .split("\n")
              .filter(Boolean)
              .map((p, i) => (
                <p key={i} data-testid={`blog-detail-paragraph-${i}`} className="text-base sm:text-lg text-[#6E675F] font-sans font-light leading-relaxed">
                  {p}
                </p>
              ))}
          </div>
          {backlinks.length > 0 && (
            <div data-testid="blog-detail-backlinks" className="mt-10 pt-6 border-t border-[#E5DED4]">
              <p className="text-[11px] uppercase tracking-[0.2em] font-sans font-semibold text-[#9A7B66] mb-3">{t("blog.related")}</p>
              <ul className="space-y-2.5">
                {backlinks.map((l, i) => (
                  <li key={i}>
                    <a
                      href={l.url}
                      target={l.url.startsWith("http") ? "_blank" : undefined}
                      rel="noopener"
                      className="inline-flex items-center gap-2 text-sm font-sans text-[#9A7B66] underline underline-offset-4 decoration-[#9A7B66]/50 hover:text-[#826552] transition-colors"
                    >
                      <Link2 className="w-3.5 h-3.5" />
                      {l.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </motion.article>
      </main>

      <Footer settings={settings || {}} />
    </div>
  );
}

export default function BlogDetail() {
  return (
    <LanguageProvider>
      <BlogDetailInner />
    </LanguageProvider>
  );
}
